// Must run before anything else so polyfilled globals exist before pdfjs loads.
import '@/lib/polyfills';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { config } from '@/config';
import {
  handlePreloadError,
  type PreloadErrorPayload,
} from '@/lib/preloadError';
// Imported for the `Sentry.init` side effect: nothing below can report until it
// has run. Explicit even though `@/lib/preloadError` also pulls it in, so the
// ordering doesn't rest on a transitive import.
import '@/lib/sentry';

// throw on invalid luxon dates after Sentry is initialized
// eslint-disable-next-line import-x/order
import { Settings } from 'luxon';
Settings.throwOnInvalid = true;

import { App } from './App';

// eslint-disable-next-line import-x/no-unresolved
import 'unfonts.css'; // vite fonts loader

import 'react-datepicker/dist/react-datepicker.css';
import './index.css';

// ensure app.agendahero.com is not accessed directly
const ensureDomain = () => {
  const { location } = window;
  if (
    config.env === 'production' &&
    !config.isLocalProdBuild &&
    !location.href.startsWith(config.server.url) &&
    !location.hostname.includes(config.server.pr)
  ) {
    const redirectUrl = new URL(location.href);
    const serverUrl = new URL(config.server.url);
    redirectUrl.host = serverUrl.host;
    redirectUrl.port = serverUrl.port;
    window.location.href = redirectUrl.toString();
  }
};
ensureDomain();

window.addEventListener('vite:preloadError', (err) => {
  handlePreloadError(err.payload as PreloadErrorPayload | undefined);
});

// https://medium.com/quick-code/100vh-problem-with-ios-safari-92ab23c852a8
const setAppHeight = () => {
  document.documentElement.style.setProperty(
    '--app-height',
    `${window.innerHeight}px`
  );
};

// iOS shrinks only the visual viewport for the keyboard, so --app-height can't see it.
const setViewportInsets = () => {
  const { visualViewport } = window;
  const { style } = document.documentElement;

  style.setProperty(
    '--visual-viewport-height',
    `${visualViewport?.height ?? window.innerHeight}px`
  );
  style.setProperty(
    '--keyboard-inset',
    visualViewport
      ? `${Math.max(
          0,
          window.innerHeight - visualViewport.height - visualViewport.offsetTop
        )}px`
      : '0px'
  );
};

window.addEventListener('resize', () => {
  setAppHeight();
  setViewportInsets();
});
window.visualViewport?.addEventListener('resize', setViewportInsets);
window.visualViewport?.addEventListener('scroll', setViewportInsets);
setAppHeight();
setViewportInsets();

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
